import React, { useState, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { format, formatDistanceToNow } from 'date-fns';

interface AuditLog {
  id: string;
  user_id: string;
  action: string;
  resource_type: string;
  resource_id: string;
  timestamp: string;
  details: any;
}

interface FilterState {
  user_id: string;
  resource_type: string;
  action: string;
  start_date: string;
  end_date: string;
}

interface AdminLogsProps {
  standalone?: boolean;
}

type SortKey = 'timestamp' | 'action' | 'resource_type';
type SortDir = 'asc' | 'desc';

// ── Visual config ──────────────────────────────────────────────────────

const actionStyle: Record<string, { dark: string; light: string; icon: string }> = {
  create:         { dark: 'bg-emerald-900/40 text-emerald-300', light: 'bg-emerald-50 text-emerald-700', icon: '+' },
  role_assign:    { dark: 'bg-purple-900/40 text-purple-300',   light: 'bg-purple-50 text-purple-700',   icon: '\u2194' },
  password_reset: { dark: 'bg-amber-900/40 text-amber-300',     light: 'bg-amber-50 text-amber-700',     icon: '\u26BF' },
  revoke:         { dark: 'bg-red-900/40 text-red-300',         light: 'bg-red-50 text-red-700',         icon: '\u2715' },
  delete:         { dark: 'bg-red-900/40 text-red-300',         light: 'bg-red-50 text-red-700',         icon: '\u2212' },
  disable:        { dark: 'bg-red-900/40 text-red-300',         light: 'bg-red-50 text-red-700',         icon: '\u25CB' },
  enable:         { dark: 'bg-emerald-900/40 text-emerald-300', light: 'bg-emerald-50 text-emerald-700', icon: '\u25CF' },
  update:         { dark: 'bg-blue-900/40 text-blue-300',       light: 'bg-blue-50 text-blue-700',       icon: '\u270E' },
};
const defaultStyle = { dark: 'bg-gray-700 text-gray-300', light: 'bg-gray-100 text-gray-600', icon: '\u2022' };

const resourceIcons: Record<string, string> = {
  user: '\u{1F464}', persona: '\u{1F916}', invite: '\u2709', template: '\u{1F4CB}',
  session: '\u{1F4AC}', artifact: '\u{1F4E6}', workflow: '\u2699',
};

// Row accent — subtle left border color per action
const rowAccent: Record<string, string> = {
  create: 'border-l-2 border-emerald-500/40',
  role_assign: 'border-l-2 border-purple-500/40',
  password_reset: 'border-l-2 border-amber-500/40',
  delete: 'border-l-2 border-red-500/40',
  revoke: 'border-l-2 border-red-500/40',
  disable: 'border-l-2 border-red-500/40',
  enable: 'border-l-2 border-emerald-500/40',
  update: 'border-l-2 border-blue-500/40',
};

function summarize(log: AuditLog): string {
  const d = log.details;
  if (!d || Object.keys(d).length === 0) return log.resource_id?.substring(0, 8) || '—';
  if (log.action === 'role_assign') return `${d.previous_role || '?'} \u2192 ${d.role}`;
  if (log.action === 'create' && d.email) return d.email;
  if (log.action === 'create' && d.name) return d.name;
  if (log.action === 'password_reset') return d.method || 'admin triggered';
  if (d.username) return d.username;
  if (d.via === 'admin_chat') return 'via Admin Chat';
  return log.resource_id?.substring(0, 8) || '—';
}

// ── Component ──────────────────────────────────────────────────────────

export const AdminLogs: React.FC<AdminLogsProps> = ({ standalone = false }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [filters, setFilters] = useState<FilterState>({
    user_id: '', resource_type: '', action: '', start_date: '', end_date: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('timestamp');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const {
    data: logsData,
    isLoading,
    refetch,
    error
  } = useQuery<{ logs: AuditLog[], total: number }>(
    ['adminAuditLogs', page, pageSize, filters],
    async () => {
      const params = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      };
      const [logsResp, countResp] = await Promise.all([
        api.get('/admin/audit-logs', { params }),
        api.get('/admin/audit-logs/count', { params }),
      ]);
      return { logs: logsResp.data, total: countResp.data.count };
    },
    { keepPreviousData: true, retry: false }
  );

  // Client-side sort (within the page)
  const sortedLogs = useMemo(() => {
    if (!logsData?.logs) return [];
    const sorted = [...logsData.logs].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'timestamp') cmp = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      else if (sortKey === 'action') cmp = a.action.localeCompare(b.action);
      else if (sortKey === 'resource_type') cmp = a.resource_type.localeCompare(b.resource_type);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [logsData?.logs, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };
  const handleApply = () => { setPage(1); refetch(); };
  const handleReset = () => {
    setFilters({ user_id: '', resource_type: '', action: '', start_date: '', end_date: '' });
    setPage(1); refetch();
  };

  const totalPages = logsData ? Math.ceil(logsData.total / pageSize) : 0;
  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  const inputClass = `w-full px-3 py-1.5 text-sm border rounded-lg ${
    darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
  }`;

  const sortArrow = (key: SortKey) => {
    if (sortKey !== key) return <span className="opacity-30 ml-1">{'\u2195'}</span>;
    return <span className="ml-1">{sortDir === 'asc' ? '\u2191' : '\u2193'}</span>;
  };

  const thClass = `px-4 py-2.5 text-left text-[10px] uppercase tracking-wider font-semibold cursor-pointer select-none transition-colors ${
    darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
  }`;

  if (error) {
    return (
      <div className={`rounded-xl p-6 ${darkMode ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-600'}`}>
        <p className="text-sm font-medium mb-2">Failed to load audit logs</p>
        <button onClick={() => refetch()} className={`text-xs px-3 py-1.5 rounded-lg ${darkMode ? 'bg-red-800 hover:bg-red-700 text-white' : 'bg-red-100 hover:bg-red-200 text-red-700'}`}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Audit Log</h2>
          <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
            {logsData?.total ?? '...'} entries
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/admin/chat', { state: { prompt: 'Summarize the audit log for the last 7 days — highlight key actions, most active users, and anything unusual' } })}
            className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${darkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}
          >
            Summarize with AI
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
              showFilters || activeFilterCount > 0
                ? darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-700'
                : darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className={`rounded-xl p-4 mb-4 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
            <div>
              <label className={`block text-[10px] uppercase tracking-wider font-medium mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>User ID</label>
              <input type="text" name="user_id" value={filters.user_id} onChange={handleFilterChange} placeholder="UUID" className={inputClass} />
            </div>
            <div>
              <label className={`block text-[10px] uppercase tracking-wider font-medium mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Resource</label>
              <select name="resource_type" value={filters.resource_type} onChange={handleFilterChange} className={inputClass}>
                <option value="">All</option>
                {['user', 'persona', 'invite', 'template', 'session', 'artifact'].map(t => (
                  <option key={t} value={t}>{resourceIcons[t] || ''} {t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-[10px] uppercase tracking-wider font-medium mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Action</label>
              <select name="action" value={filters.action} onChange={handleFilterChange} className={inputClass}>
                <option value="">All</option>
                {Object.keys(actionStyle).map(a => (
                  <option key={a} value={a}>{a.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-[10px] uppercase tracking-wider font-medium mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>From</label>
              <input type="date" name="start_date" value={filters.start_date} onChange={handleFilterChange} className={inputClass} />
            </div>
            <div>
              <label className={`block text-[10px] uppercase tracking-wider font-medium mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>To</label>
              <input type="date" name="end_date" value={filters.end_date} onChange={handleFilterChange} className={inputClass} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={handleReset} className={`text-xs px-3 py-1 rounded-lg ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>Reset</button>
            <button onClick={handleApply} className={`text-xs px-3 py-1.5 rounded-lg font-medium ${darkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}>Apply</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-red-500"></div>
          </div>
        ) : !sortedLogs.length ? (
          <div className={`text-center py-12 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            No audit log entries found.
          </div>
        ) : (
          <>
            <table className="w-full">
              {/* Column headers — clickable for sorting */}
              <thead>
                <tr className={darkMode ? 'bg-gray-900/40' : 'bg-gray-50'}>
                  <th className={`${thClass} w-[140px]`} onClick={() => toggleSort('timestamp')}>
                    Time {sortArrow('timestamp')}
                  </th>
                  <th className={`${thClass} w-[110px]`} onClick={() => toggleSort('action')}>
                    Action {sortArrow('action')}
                  </th>
                  <th className={`${thClass} w-[100px]`} onClick={() => toggleSort('resource_type')}>
                    Resource {sortArrow('resource_type')}
                  </th>
                  <th className={thClass}>Summary</th>
                  <th className={`${thClass} w-[100px]`}>User</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-gray-700/30' : 'divide-gray-100'}`}>
                {sortedLogs.map((log) => {
                  const as = actionStyle[log.action] || defaultStyle;
                  const accent = rowAccent[log.action] || 'border-l-2 border-transparent';
                  const isExpanded = expandedLog === log.id;
                  const ri = resourceIcons[log.resource_type] || '\u2022';
                  const hasChatBadge = log.details?.via === 'admin_chat';

                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        className={`${accent} transition-colors cursor-pointer ${
                          isExpanded
                            ? darkMode ? 'bg-gray-700/20' : 'bg-gray-50'
                            : darkMode ? 'hover:bg-gray-700/20' : 'hover:bg-gray-50/80'
                        }`}
                        onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                      >
                        {/* Timestamp — two-line: relative + absolute */}
                        <td className="px-4 py-2.5 align-top">
                          <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                          </div>
                          <div className={`text-[10px] font-mono ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                            {format(new Date(log.timestamp), 'MMM d, HH:mm')}
                          </div>
                        </td>

                        {/* Action badge */}
                        <td className="px-4 py-2.5 align-top">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${darkMode ? as.dark : as.light}`}>
                            <span className="text-[11px]">{as.icon}</span>
                            {log.action.replace('_', ' ')}
                          </span>
                        </td>

                        {/* Resource type with icon */}
                        <td className="px-4 py-2.5 align-top">
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            <span className="mr-1">{ri}</span>{log.resource_type}
                          </span>
                        </td>

                        {/* Summary — the meat of the row */}
                        <td className="px-4 py-2.5 align-top">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                              {summarize(log)}
                            </span>
                            {hasChatBadge && (
                              <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-500'}`}>
                                AI
                              </span>
                            )}
                          </div>
                          {/* Resource ID as subtle subtext */}
                          <div className={`text-[10px] font-mono mt-0.5 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                            {log.resource_id?.substring(0, 12)}
                          </div>
                        </td>

                        {/* User — truncated ID */}
                        <td className={`px-4 py-2.5 align-top text-[11px] font-mono ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {log.user_id?.substring(0, 8)}
                        </td>
                      </tr>

                      {/* Expanded details row */}
                      {isExpanded && (
                        <tr className={darkMode ? 'bg-gray-900/30' : 'bg-gray-50'}>
                          <td colSpan={5} className="px-4 py-3">
                            <div className={`text-xs rounded-lg p-3 ${darkMode ? 'bg-gray-900/60' : 'bg-white border border-gray-100'}`}>
                              <div className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-1.5">
                                <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Full User ID</span>
                                <span className={`font-mono ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{log.user_id}</span>
                                <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Full Resource ID</span>
                                <span className={`font-mono ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{log.resource_id}</span>
                                <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Full Timestamp</span>
                                <span className={`font-mono ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{format(new Date(log.timestamp), 'yyyy-MM-dd HH:mm:ss.SSS')}</span>
                                {log.details && Object.entries(log.details).map(([key, val]) => (
                                  <React.Fragment key={key}>
                                    <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>{key}</span>
                                    <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                                      {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                                    </span>
                                  </React.Fragment>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <div className={`px-4 py-3 flex items-center justify-between border-t ${darkMode ? 'border-gray-700/30' : 'border-gray-100'}`}>
              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, logsData!.total)} of {logsData!.total}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`text-xs px-2.5 py-1 rounded-lg disabled:opacity-30 ${darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  Prev
                </button>
                <span className={`text-xs px-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{page}/{totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className={`text-xs px-2.5 py-1 rounded-lg disabled:opacity-30 ${darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminLogs;
